import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { DeviceTokenService } from '@app/shared/services/device-token.service';
import { MinimalVerifyResponse } from '@app/shared/interfaces/tiered-verify';

export type DirectiveType = 'md_advance_directive' | 'va_10_0137' | 'md_molst';

export type Election = 'yes' | 'no' | 'unsure';

/** What the patient chose for a given condition on the Maryland form. */
export type MdTreatmentChoice = 'comfort_only' | 'trial_then_stop' | 'all_treatment' | 'unsure';

export interface AgentDetails {
  name: string;
  relationship?: string;
  address?: string;
  phone?: string;
  /** Maryland eligibility as the patient confirmed it — see §5-602. */
  isAdult?: boolean;
  facilityRelation?: string;
  protectiveOrder?: boolean;
  spouseStatus?: string;
}

export interface Demographics {
  fullName: string;
  /** Omitted when the patient's account has no DOB yet — never sent as ''. */
  dateOfBirth?: string;
  address?: string;
  phone?: string;
}

export interface VaDirectivePayload {
  demographics: Demographics;
  healthCareAgent?: AgentDetails;
  alternateAgent?: AgentDetails;
  livingWill?: {
    cpr?: Election;
    mechanicalVentilation?: Election;
    feedingTube?: Election;
    dialysis?: Election;
    comfortCareOnly?: boolean;
    otherInstructions?: string;
  };
  additionalWishes?: string;
}

/** The Maryland Advance Directive: agent appointment + living-will wishes. */
export interface MdDirectivePayload {
  demographics: Demographics;
  healthCareAgent?: AgentDetails;
  /** First backup agent. */
  alternateAgent?: AgentDetails;
  /** Second backup agent — Maryland's form has room for two, both optional. */
  secondAlternateAgent?: AgentDetails;
  agentAuthority?: {
    /** Part I H — when the agent's power begins. */
    effective?: 'immediately' | 'on_incapacity';
    limitations?: string;
    /** Part I E — people the agent should consult. No authority of their own. */
    consultPeople?: { name: string; phone?: string }[];
    /**
     * Part I G — present ONLY when the patient opted out. Absence means the
     * standard authorization applies, exactly as signing the paper form does.
     */
    healthInfoOptOut?: boolean;
  };
  livingWill?: {
    cpr?: Election;
    unconsciousNoRecovery?: MdTreatmentChoice;
    severeBrainDamage?: MdTreatmentChoice;
    permanentDependence?: MdTreatmentChoice;
    /** Maryland's statutory end-stage condition — distinct from permanentDependence. */
    endStageCondition?: MdTreatmentChoice;
    ventilatorDependent?: MdTreatmentChoice;
    unrelievableSuffering?: MdTreatmentChoice;
    imminentDeath?: MdTreatmentChoice;
    artificialNutrition?: MdTreatmentChoice;
    /** Confirmed at signing: no tube feeding even where all treatment was requested. */
    noTubeFeedingEvenIfProlonging?: boolean;
    otherCondition?: string;
    /** Instructions that apply if the patient is pregnant. */
    pregnancyInstructions?: string;
  };
  mentalHealth?: {
    preferences?: string[];
    additionalPreferences?: string[];
  };
  organDonation?: {
    donor?: boolean;
    limitations?: string;
  };
  /**
   * What this document does to any earlier advance directive. `precedence`
   * and its `textVersion` are the patient's declaration made at signing;
   * `madeEarlierDirective` is the questionnaire's fact, filled by the ACP
   * bridge on the server.
   */
  priorDirectives?: {
    madeEarlierDirective?: boolean;
    precedence?: MdPriorPrecedence;
    textVersion?: string;
    details?: string;
  };
  instructionsFollowing?: string;
  additionalWishes?: string;
  filedWithStateRegistry?: boolean;
}

export type MdPriorPrecedence = 'no_prior' | 'revoke_all_prior' | 'supplement_prior';

export interface DirectiveSignatureView {
  role: string;
  signerName: string;
  signatureHash: string;
  signedAt: string;
  /** How this signature came about — see the signing-provenance work. */
  presenceMode?: 'in_person' | 'electronic_presence' | null;
  signingChannel?: 'handover' | 'own_device' | 'declarant_session' | null;
  attestationSource?: 'signer' | 'declarant' | null;
  notEntitledToEstate?: boolean | null;
  blockchainTxId?: string | null;
}

export interface Directive {
  id: string;
  docType: DirectiveType;
  status: 'draft' | 'pending_signatures' | 'active' | 'superseded' | 'revoked';
  version: number;
  qrToken: string;
  documentHash: string | null;
  blockchainStatus: string;
  blockchainTxId: string | null;
  payload: (VaDirectivePayload | MdDirectivePayload) | null;
  signatures: DirectiveSignatureView[];
  missingSignatures: string[];
  createdAt: string;
}

export interface VerifiedDirective extends Directive {
  /** Tiered disclosure: present ('full') when a registered device asked; legacy responses omit it. */
  tier?: 'full';
  integrityVerified: boolean;
  integrityResult: 'pass' | 'fail' | 'local_only' | 'unknown';
  isCurrentVersion: boolean;
  anchoredTxId: string | null;
}

/** Kept so existing VA screens compile unchanged while VA is being retired. */
export type VaDirective = Directive;
export type VerifiedVaDirective = VerifiedDirective;

/**
 * The free-text fields of a directive payload, whichever type it is. The VA
 * form calls its instructions field `otherInstructions` and Maryland calls it
 * `otherCondition`, so callers looking for written content (such as the
 * questionnaire block "Apply my answers" leaves behind) should ask here rather
 * than reach into one type's shape.
 */
export function directiveFreeText(payload: Directive['payload']): string[] {
  const livingWill = (payload?.livingWill ?? {}) as Record<string, unknown>;
  return [livingWill.otherInstructions, livingWill.otherCondition, payload?.additionalWishes].filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
}

/** How the witness was present — Health-General §5-602. */
export type PresenceMode = 'in_person' | 'electronic_presence';

export interface SignRequest {
  role: 'patient' | 'witness_1' | 'witness_2' | 'notary';
  signerName: string;
  signatureData: string;
  signerMeta?: Record<string, unknown>;
  /** The handover this witness is signing under. Required for witnesses. */
  handoverSessionId?: string;
  presenceMode?: PresenceMode;
  /** The signing ceremony (Maryland): accepted declaration + patient reauth. */
  declarationAccepted?: boolean;
  declarationTextVersion?: string;
  password?: string;
  otpCode?: string;
  signerTimezone?: string;
}

/**
 * The exact declarations signers accept — mirrors the backend's
 * attestation-texts.ts. The version travels with every signature so the
 * record always says which text the person saw.
 */
export const MD_DECLARATION_TEXT_VERSION = 'md-2026-08-v2';

export const MD_PATIENT_DECLARATION =
  'I voluntarily make this Maryland Advance Directive while capable of making and communicating ' +
  'an informed decision. I understand the purpose and effect of this document. I have reviewed ' +
  'the complete document, and I intend my electronic signature below to authenticate and sign it.';

/** v2: the witness now states their own eligibility (18+, competent) too. */
export const MD_WITNESS_DECLARATION =
  'The declarant signed or acknowledged signing this complete advance directive in my physical ' +
  'presence. Based on my personal observation, the declarant appeared capable of making and ' +
  'communicating an informed decision and appeared to sign voluntarily. I am not a primary or ' +
  'backup health care agent appointed in this document. I am at least 18 years old and competent ' +
  'and eligible to serve as a witness to this advance directive. I have reviewed this witness ' +
  'declaration and intend my electronic signature below to authenticate and sign it.';

/**
 * The prior-directive precedence statements — mirrors the backend's
 * attestation-texts.ts. The patient adopts one verbatim before signing; the
 * chosen code and this version freeze onto the payload at submit.
 */
export const MD_PRECEDENCE_TEXT_VERSION = 'md-2026-08-v1';

export const MD_PRECEDENCE_STATEMENTS: { value: MdPriorPrecedence; text: string }[] = [
  {
    value: 'no_prior',
    text: 'I have not made any other advance directive. This document is my only advance directive.',
  },
  {
    value: 'revoke_all_prior',
    text:
      'I revoke every advance directive I have made before this one, wherever it is kept. ' +
      'This document replaces them all.',
  },
  {
    value: 'supplement_prior',
    text:
      'I have an earlier advance directive that I have not revoked. This document adds to that ' +
      'earlier directive, and wherever the two differ, this newer document is the one to follow.',
  },
];

export interface HandoverSession {
  handoverSessionId: string;
  expiresAt: string;
  role: 'witness_1' | 'witness_2';
  /**
   * Whether the declarant has a PIN, so the return step asks for the right
   * credential. Accounts imported from the Epic sandbox have none.
   */
  declarantHasPin: boolean;
}

/** What the declarant offers to get their device back. */
export interface HandoverReturn {
  pin?: string;
  password?: string;
  /** Sets a PIN for next time. Ignored when the account already has one. */
  newPin?: string;
}

/**
 * Advance-directive API. One service for both patient-authored types: the
 * Maryland Advance Directive and (until it is retired) VA Form 10-0137.
 * Only create/update are type-specific; reading, submitting, signing,
 * verifying and printing are shared.
 */
@Injectable({ providedIn: 'root' })
export class DirectiveService {
  private readonly base = `${environment.apiUrl}/directives`;

  constructor(
    private readonly http: HttpClient,
    private readonly deviceTokens: DeviceTokenService,
  ) {}

  public listMine(): Observable<Directive[]> {
    return this.http.get<Directive[]>(`${this.base}/mine`);
  }

  /**
   * The directive in effect for a patient who has shared it with me.
   * 403 when they have not; an empty body when they have none yet.
   */
  public sharedCurrent(patientId: number): Observable<Directive | null> {
    return this.http.get<Directive | null>(`${this.base}/shared/${patientId}/current`);
  }

  public get(id: string): Observable<Directive> {
    return this.http.get<Directive>(`${this.base}/${id}`);
  }

  // ------------------------------------------------------------- Maryland

  public createMd(payload: MdDirectivePayload): Observable<Directive> {
    return this.http.post<Directive>(`${this.base}/md`, { payload });
  }

  // ------------------------------------------------------------------- VA

  public create(payload: VaDirectivePayload): Observable<Directive> {
    return this.http.post<Directive>(`${this.base}/va`, { payload });
  }

  // --------------------------------------------------------------- shared

  /**
   * Update a draft. The document already carries its own type, so there is
   * one update route; the backend validates against that type's schema.
   */
  public update(id: string, payload: VaDirectivePayload | MdDirectivePayload): Observable<Directive> {
    return this.http.put<Directive>(`${this.base}/${id}`, { payload });
  }

  /** Named for symmetry with createMd; the same shared route. */
  public updateMd(id: string, payload: MdDirectivePayload): Observable<Directive> {
    return this.update(id, payload);
  }

  public submit(id: string): Observable<Directive> {
    return this.http.post<Directive>(`${this.base}/${id}/submit`, {});
  }

  /**
   * Record one signature. The backend answers 422 when the signer is not
   * eligible — for example the appointed agent trying to witness a Maryland
   * directive — and that message is written to be shown to the patient.
   */
  public sign(id: string, request: SignRequest): Observable<Directive> {
    return this.http.post<Directive>(`${this.base}/${id}/sign`, request);
  }

  /**
   * Hand the device to a witness. Opens the short-lived session their
   * signature is recorded under, so their confirmation is attributed to them
   * and not to whoever's account happens to be signed in.
   */
  public beginHandover(
    id: string,
    role: 'witness_1' | 'witness_2',
    patientAcknowledged = false,
  ): Observable<HandoverSession> {
    return this.http.post<HandoverSession>(`${this.base}/${id}/handover/begin`, {
      role,
      patientAcknowledged,
    });
  }

  /**
   * Take the device back. Either credential proves it is the account holder;
   * the password is the way in for an account with no PIN, so a declarant can
   * never be locked out of their own device.
   */
  public endHandover(
    id: string,
    handoverSessionId: string,
    credential: HandoverReturn,
  ): Observable<unknown> {
    return this.http.post(`${this.base}/${id}/handover/end`, { handoverSessionId, ...credential });
  }

  /**
   * Public break-glass verify — no auth header needed (interceptor adds one
   * harmlessly). Tiered disclosure: a REGISTERED device attaches its
   * X-Device-Token and receives the full directive; otherwise the backend
   * returns the minimal emergency summary.
   */
  public verify(qrToken: string): Observable<VerifiedDirective | MinimalVerifyResponse> {
    const deviceToken = this.deviceTokens.getToken();
    const options = deviceToken
      ? { headers: new HttpHeaders({ 'X-Device-Token': deviceToken }) }
      : {};
    return this.http.get<VerifiedDirective | MinimalVerifyResponse>(
      `${this.base}/verify/${qrToken}`,
      options,
    );
  }

  /** The printable directive: the official VA form, or the Maryland printout. */
  public downloadPdf(id: string): Observable<Blob> {
    return this.http.get(`${this.base}/${id}/pdf`, { responseType: 'blob' });
  }

  /** The one-page Quick View of the same sealed version (Maryland only). */
  public downloadQuickView(id: string): Observable<Blob> {
    return this.http.get(`${this.base}/${id}/quick-view`, { responseType: 'blob' });
  }

  /**
   * Revoke a directive — §5-604's signed electronic revocation. Requires the
   * account password (and TOTP when enrolled); the version is kept forever
   * and every verify surface reports REVOKED.
   */
  public revoke(id: string, password: string, otpCode?: string): Observable<Directive> {
    return this.http.post<Directive>(`${this.base}/${id}/revoke`, {
      password,
      ...(otpCode ? { otpCode } : {}),
    });
  }
}
